// painel-admin/src/pages/TikpagExperience/TikpagExperienceDashboard.jsx

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Award,
  CalendarDays,
  CircleDollarSign,
  Gift,
  Medal,
  RefreshCcw,
  Sparkles,
  Trophy,
  UserCheck
} from "lucide-react";

import axios from "../../axiosConfig";
import "./TikpagExperienceDashboard.css";

const formatarDinheiro = (valor) =>
  Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

const formatarData = (valor) => {
  if (!valor) return "-";

  return new Date(valor).toLocaleDateString("pt-BR", {
    timeZone: "UTC"
  });
};

const formatarCompetencia = (ano, mes) =>
  `${String(mes).padStart(2, "0")}/${ano}`;

const obterClasseStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

export default function TikpagExperienceDashboard() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const carregarDashboard = useCallback(async () => {
    setLoading(true);
    setErro("");

    try {
      const resposta = await axios.get(
        "/admin/experience/dashboard"
      );

      setDados(resposta.data || null);
    } catch (err) {
      console.error(
        "Erro ao carregar dashboard Tikpag Experience:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível carregar o dashboard do Tikpag Experience."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDashboard();
  }, [carregarDashboard]);

  const campanha = dados?.campanha || null;
  const cards = dados?.cards || {};
  const pins = Array.isArray(dados?.pins)
    ? dados.pins
    : [];

  const fechamentos = Array.isArray(
    dados?.ultimos_fechamentos
  )
    ? dados.ultimos_fechamentos
    : [];

  const escolhas = Array.isArray(
    dados?.escolhas_recentes
  )
    ? dados.escolhas_recentes
    : [];

  const cardsResumo = [
    {
      titulo: "Inscritos",
      valor: Number(cards.inscritos || 0),
      legenda: "Participantes ativos",
      icon: UserCheck
    },
    {
      titulo: "Volume apurado",
      valor: formatarDinheiro(
        cards.volume_total_apurado
      ),
      legenda: "Somatório das apurações",
      icon: CircleDollarSign
    },
    {
      titulo: "Premiados",
      valor: Number(cards.premiados || 0),
      legenda: "Conquistas emitidas",
      icon: Trophy
    },
    {
      titulo: "Experiências ativas",
      valor: Number(
        cards.experiencias_ativas || 0
      ),
      legenda: "Opções disponíveis",
      icon: Gift
    },
    {
      titulo: "PINs ativos",
      valor: Number(cards.pins_ativos || 0),
      legenda: "Faixas configuradas",
      icon: Medal
    },
    {
      titulo: "Aguardando escolha",
      valor: Number(
        cards.aguardando_escolha || 0
      ),
      legenda: "Clientes pendentes",
      icon: Award
    },
    {
      titulo: "Fechamentos",
      valor: Number(
        cards.fechamentos_concluidos || 0
      ),
      legenda: "Meses concluídos",
      icon: CalendarDays
    },
    {
      titulo: "PINs pendentes",
      valor: Number(cards.pins_pendentes || 0),
      legenda: "Produção ou entrega",
      icon: Sparkles
    }
  ];

  return (
    <div className="experienceDashboard-page">
      <header className="experienceDashboard-header">
        <div>
          <span className="experienceDashboard-kicker">
            Tikpag Experience
          </span>

          <h1>Dashboard</h1>

          <p>
            Visão geral das campanhas, participantes,
            PINs, experiências e fechamentos mensais.
          </p>
        </div>

        <div className="experienceDashboard-headerActions">
          <button
            type="button"
            className="experienceDashboard-secondaryButton"
            onClick={carregarDashboard}
            disabled={loading}
          >
            <RefreshCcw
              size={16}
              className={
                loading
                  ? "experienceDashboard-spin"
                  : ""
              }
            />

            Atualizar
          </button>

          <Link
            to="/admin/tikpag-experience/campanhas"
            className="experienceDashboard-primaryButton"
          >
            <Sparkles size={16} />
            Gerenciar campanhas
          </Link>
        </div>
      </header>

      {erro && (
        <div className="experienceDashboard-alert">
          {erro}
        </div>
      )}

      {campanha ? (
        <section className="experienceDashboard-campaign">
          <div className="experienceDashboard-campaignIdentity">
            <div className="experienceDashboard-campaignIcon">
              <Sparkles size={22} />
            </div>

            <div>
              <span>Campanha selecionada</span>
              <h2>{campanha.nome}</h2>

              <p>
                {formatarData(campanha.data_inicio)}
                {" até "}
                {formatarData(campanha.data_fim)}
                {" · "}
                {campanha.periodicidade}
              </p>
            </div>
          </div>

          <span
            className={`experienceDashboard-status ${obterClasseStatus(
              campanha.status
            )}`}
          >
            {campanha.status}
          </span>
        </section>
      ) : (
        !loading && (
          <section className="experienceDashboard-emptyCampaign">
            <Sparkles size={26} />

            <div>
              <h2>Nenhuma campanha disponível</h2>

              <p>
                Cadastre ou ative uma campanha para
                começar a utilizar o Tikpag Experience.
              </p>
            </div>

            <Link
              to="/admin/tikpag-experience/campanhas"
              className="experienceDashboard-primaryButton"
            >
              Criar campanha
            </Link>
          </section>
        )
      )}

      <section className="experienceDashboard-summary">
        {cardsResumo.map((card) => {
          const Icon = card.icon;

          return (
            <article
              className="experienceDashboard-summaryCard"
              key={card.titulo}
            >
              <div className="experienceDashboard-summaryIcon">
                <Icon size={20} />
              </div>

              <div>
                <span>{card.titulo}</span>
                <strong>{card.valor}</strong>
                <small>{card.legenda}</small>
              </div>
            </article>
          );
        })}
      </section>

      <section className="experienceDashboard-panel">
        <div className="experienceDashboard-panelHeader">
          <div>
            <h2>PINs da campanha</h2>

            <p>
              Metas mensais, experiências disponíveis
              e conquistas emitidas.
            </p>
          </div>

          <Link
            to="/admin/tikpag-experience/pins"
            className="experienceDashboard-secondaryButton"
          >
            Gerenciar PINs
          </Link>
        </div>

        <div className="experienceDashboard-pinGrid">
          {pins.map((pin) => (
            <article
              className="experienceDashboard-pinCard"
              key={pin.id}
            >
              <div className="experienceDashboard-pinTop">
                <div className="experienceDashboard-pinIcon">
                  {pin.imagem_url ? (
                    <img
                      src={pin.imagem_url}
                      alt={pin.nome}
                    />
                  ) : (
                    <Medal size={24} />
                  )}
                </div>

                <span
                  className={`experienceDashboard-smallStatus ${
                    pin.ativo ? "ativo" : "inativo"
                  }`}
                >
                  {pin.ativo ? "Ativo" : "Inativo"}
                </span>
              </div>

              <h3>{pin.nome}</h3>

              <p className="experienceDashboard-pinValue">
                {formatarDinheiro(pin.valor_minimo)}
              </p>

              <div className="experienceDashboard-pinMetrics">
                <div>
                  <span>Experiências</span>
                  <strong>
                    {Number(
                      pin.experiencias_disponiveis ||
                        0
                    )}
                  </strong>
                </div>

                <div>
                  <span>Conquistas</span>
                  <strong>
                    {Number(pin.conquistas || 0)}
                  </strong>
                </div>

                <div>
                  <span>Pendentes</span>
                  <strong>
                    {Number(
                      pin.aguardando_escolha || 0
                    )}
                  </strong>
                </div>
              </div>
            </article>
          ))}

          {!loading && !pins.length && (
            <div className="experienceDashboard-empty">
              Nenhum PIN cadastrado para esta campanha.
            </div>
          )}
        </div>
      </section>

      <div className="experienceDashboard-bottomGrid">
        <section className="experienceDashboard-panel">
          <div className="experienceDashboard-panelHeader">
            <div>
              <h2>Últimos fechamentos</h2>

              <p>
                Histórico recente das apurações mensais.
              </p>
            </div>

            <Link
              to="/admin/tikpag-experience/apuracoes"
              className="experienceDashboard-textLink"
            >
              Ver apurações
            </Link>
          </div>

          {fechamentos.length ? (
            <div className="experienceDashboard-tableWrapper">
              <table className="experienceDashboard-table">
                <thead>
                  <tr>
                    <th>Competência</th>
                    <th>Status</th>
                    <th>Participantes</th>
                    <th>Premiados</th>
                    <th>Volume</th>
                  </tr>
                </thead>

                <tbody>
                  {fechamentos.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {formatarCompetencia(
                          item.ano,
                          item.mes
                        )}
                      </td>

                      <td>
                        <span
                          className={`experienceDashboard-smallStatus ${obterClasseStatus(
                            item.status
                          )}`}
                        >
                          {item.status}
                        </span>
                      </td>

                      <td>
                        {Number(
                          item.total_participantes ||
                            0
                        )}
                      </td>

                      <td>
                        {Number(
                          item.total_premiados || 0
                        )}
                      </td>

                      <td>
                        {formatarDinheiro(
                          item.volume_total
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="experienceDashboard-empty">
              Nenhum fechamento executado.
            </div>
          )}
        </section>

        <section className="experienceDashboard-panel">
          <div className="experienceDashboard-panelHeader">
            <div>
              <h2>Escolhas recentes</h2>

              <p>
                Últimas experiências escolhidas pelos
                participantes.
              </p>
            </div>

            <Link
              to="/admin/tikpag-experience/escolhas"
              className="experienceDashboard-textLink"
            >
              Ver escolhas
            </Link>
          </div>

          {escolhas.length ? (
            <div className="experienceDashboard-choiceList">
              {escolhas.map((item) => (
                <article
                  className="experienceDashboard-choice"
                  key={item.id}
                >
                  <div className="experienceDashboard-choiceIcon">
                    {item.experiencia_imagem ? (
                      <img
                        src={item.experiencia_imagem}
                        alt=""
                      />
                    ) : (
                      <Gift size={18} />
                    )}
                  </div>

                  <div>
                    <strong>{item.usuario_nome}</strong>

                    <span>
                      {item.experiencia_titulo}
                    </span>

                    <small>
                      {item.pin_nome}
                      {" · "}
                      {formatarCompetencia(
                        item.ano,
                        item.mes
                      )}
                    </small>
                  </div>

                  <span
                    className={`experienceDashboard-smallStatus ${obterClasseStatus(
                      item.status
                    )}`}
                  >
                    {item.status}
                  </span>
                </article>
              ))}
            </div>
          ) : (
            <div className="experienceDashboard-empty">
              Nenhuma experiência escolhida.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}